import React, { useState, useContext, useEffect, useMemo } from 'react';
import {
  MDBContainer,
  MDBRow,
  MDBCol,
  MDBTypography,
  MDBBtn,
  MDBCard,
  MDBCardBody,
  MDBInput,
  MDBAlert,
  MDBIcon,
  MDBBadge,
  MDBModal,
  MDBModalDialog,
  MDBModalContent,
  MDBModalHeader,
  MDBModalTitle,
  MDBModalBody,
  MDBModalFooter,
  MDBInputGroup,
  MDBTable,
  MDBTableBody,
  MDBDropdown,
  MDBDropdownToggle,
  MDBDropdownMenu,
  MDBDropdownItem
} from 'mdb-react-ui-kit';
import { useTranslation } from 'react-i18next';
import { useFormData } from '../hooks/useFormData';
import { useStepNavigation } from '../hooks/useStepNavigation';
import { useValidation } from '../components/ValidationProvider';
import { useCompanyData } from '../hooks/useCompanyData';
import { getSpeakerRequirementsForType, getLectureTypeName } from '../validation/lectures.js';
import { DevContext } from '../../../../App';
import AddSpeaker from '../../../manage-company/AddSpeaker';
import { generateClient } from 'aws-amplify/api';
import { captureError } from '../../../../utils/sentryHelpers';

const client = generateClient();

const isPersistedId = (id) => id && !(typeof id === 'string' && id.startsWith('temp-'));

// Options for the "displayed company" of an external speaker:
// the companies of their employee profiles and the company from their personal profile.
const loadAffiliationOptions = async (userId) => {
  let companies = [];
  try {
    const response = await client.graphql({
      query: /* GraphQL */ `
        query SearchSpeakerEmployeeProfiles($filter: SearchableEmployeeFilterInput, $limit: Int) {
          searchEmployees(filter: $filter, limit: $limit) {
            items {
              id
              company {
                id
                name
              }
            }
          }
        }
      `,
      variables: { filter: { userEmployeeProfilesId: { eq: userId } }, limit: 20 }
    });
    const seen = new Set();
    companies = (response.data.searchEmployees?.items || [])
      .map(item => item.company)
      .filter(company => company?.id && company?.name && !seen.has(company.id) && seen.add(company.id));
  } catch (error) {
    captureError(error, {
      feature: 'lecture-registration',
      operation: 'load-speaker-employee-profiles',
      context: { userId }
    });
  }

  // The personal profile (contact) is only readable for admins / company admins.
  // If it is not readable we still offer the option; the value is resolved server-side.
  let profileCompany = null;
  let profileReadable = false;
  const profileQuery = /* GraphQL */ `
    query GetSpeakerProfileCompany($id: ID!) {
      getUser(id: $id) {
        id
        contact {
          address {
            company
          }
        }
      }
    }
  `;
  try {
    const response = await client.graphql({ query: profileQuery, variables: { id: userId } });
    profileCompany = response.data.getUser?.contact?.address?.company || null;
    profileReadable = true;
  } catch (error) {
    profileCompany = error?.data?.getUser?.contact?.address?.company || null;
    profileReadable = !!error?.data?.getUser?.contact;
  }

  return { companies, profileCompany, profileReadable };
};

// Default choice, same priority as the automatic resolution in the sync:
// exactly one linked company other than the lecture company -> that one,
// else the personal profile, else manual input.
const defaultAffiliation = (options, lectureCompanyId) => {
  const otherCompanies = options.companies.filter(company => company.id !== lectureCompanyId);
  if (otherCompanies.length === 1) {
    return { source: 'employeeProfile', companyId: otherCompanies[0].id, companyName: otherCompanies[0].name };
  }
  if (options.profileCompany) {
    return { source: 'personalProfile', companyId: null, companyName: options.profileCompany };
  }
  return { source: 'manual', companyId: null, companyName: '' };
};


export default function SpeakersStep({ onComplete, companyId }) {
  const { t, i18n } = useTranslation();
  const { formData, handleFieldChange, hasFieldError, getFieldError } = useFormData();
  const { goToNextStep, goToPreviousStep } = useStepNavigation();
  const { getStepValidation, getStepRequiredFields, getFieldValidation, eventConfig } = useValidation();
  const { isDevMode } = useContext(DevContext);
  
  // Use the company data hook
  const { 
    employees, 
    searchEmployees: searchEmployeesFromHook,
    transformEmployeeToSpeaker,
    isLoading: isLoadingEmployees,
    reloadEmployees 
  } = useCompanyData(companyId);
  
  const [filteredEmployees, setFilteredEmployees] = useState([]);
  const [employeeSearchInput, setEmployeeSearchInput] = useState("");
  const [selectEmployeeModal, setSelectEmployeeModal] = useState(false);
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [addSpeakerModal, setAddSpeakerModal] = useState(false);
  // Options for the displayed company of external speakers, keyed by user id
  const [affiliationOptions, setAffiliationOptions] = useState({});

  const speakers = useMemo(() => formData.speakers || [], [formData.speakers]);
  
  // Get speaker requirements based on selected lecture type
  const speakerRequirements = formData.type ? getSpeakerRequirementsForType(eventConfig, formData.type) : { min: 1 };
  const minSpeakersRequired = speakerRequirements.min || 1;
  // Show notification when 2 or more speakers are required
  // This will show for VIP presentations (min 2 speakers) and any future types requiring more
  const showMinSpeakersNotification = minSpeakersRequired >= 2;
  
  // Get current language for lecture type name
  const currentLanguage = i18n.language || 'en';
  const lectureTypeName = formData.type ? getLectureTypeName(formData.type, currentLanguage) : '';

  // Load the company options for every external speaker once
  useEffect(() => {
    const pending = speakers.filter(speaker =>
      speaker.speakerType === 'user' &&
      isPersistedId(speaker.userId || speaker.id) &&
      affiliationOptions[speaker.userId || speaker.id] === undefined
    );
    if (pending.length === 0) return;

    setAffiliationOptions(old => {
      const next = { ...old };
      pending.forEach(speaker => { next[speaker.userId || speaker.id] = null; }); // null = loading
      return next;
    });

    pending.forEach(async (speaker) => {
      const userId = speaker.userId || speaker.id;
      const options = await loadAffiliationOptions(userId);
      setAffiliationOptions(old => ({ ...old, [userId]: options }));
    });
  }, [speakers, affiliationOptions]);

  // Preselect the displayed company for external speakers without a stored choice
  useEffect(() => {
    let changed = false;
    const updated = speakers.map(speaker => {
      if (speaker.speakerType !== 'user' || speaker.affiliation) return speaker;
      const options = affiliationOptions[speaker.userId || speaker.id];
      if (!options) return speaker;
      changed = true;
      return { ...speaker, affiliation: defaultAffiliation(options, companyId) };
    });
    if (changed) handleFieldChange('speakers', updated);
  }, [speakers, affiliationOptions, companyId, handleFieldChange]);

  const setSpeakerAffiliation = (speakerId, affiliation) => {
    handleFieldChange('speakers', speakers.map(speaker =>
      speaker.id === speakerId ? { ...speaker, affiliation } : speaker
    ));
  };

  const handleAffiliationSelect = (speaker, value) => {
    const options = affiliationOptions[speaker.userId || speaker.id] || { companies: [], profileCompany: null };
    if (value === 'profile') {
      setSpeakerAffiliation(speaker.id, { source: 'personalProfile', companyId: null, companyName: options.profileCompany || '' });
    } else if (value === 'manual') {
      setSpeakerAffiliation(speaker.id, { source: 'manual', companyId: null, companyName: '' });
    } else if (value.startsWith('company:')) {
      const company = options.companies.find(c => c.id === value.slice('company:'.length));
      if (company) {
        setSpeakerAffiliation(speaker.id, { source: 'employeeProfile', companyId: company.id, companyName: company.name });
      }
    }
  };

  const affiliationSelectValue = (affiliation) => {
    if (!affiliation) return '';
    if (affiliation.source === 'employeeProfile' && affiliation.companyId) return `company:${affiliation.companyId}`;
    if (affiliation.source === 'personalProfile') return 'profile';
    return 'manual';
  };

  const toggleSelectEmployeeModal = () => setSelectEmployeeModal(!selectEmployeeModal);
  const toggleAddSpeakerModal = () => {
    if (addSpeakerModal) {
      // When closing the modal, reload employees to get any newly added ones
      reloadEmployees();
      // Also close the select employee modal
      setSelectEmployeeModal(false);
    }
    setAddSpeakerModal(!addSpeakerModal);
  };

  // Update filtered employees when search input changes
  useEffect(() => {
    if (employeeSearchInput === "") {
      setFilteredEmployees([]);
    } else {
      const filtered = searchEmployeesFromHook(employeeSearchInput);
      setFilteredEmployees(filtered);
    }
  }, [employeeSearchInput, searchEmployeesFromHook]);

  const handleChangeSearchEmployeeInput = (e) => {
    setEmployeeSearchInput(e.target.value);
  };

  const selectEmployee = (employeeId) => {
    const employee = employees.find(emp => emp.id === employeeId);
    if (employee) {
      // Determine speaker type
      const isFirst = speakers.length === 0;
      const type = isFirst ? 'main_speaker' : 'co_speaker';

      // Use the transformation function from the hook
      const speakerData = transformEmployeeToSpeaker(employee, type);

      if (speakerData) {
        // Ensure speakerType is set to 'employee'
        speakerData.speakerType = 'employee';

        const updatedSpeakers = [...speakers, speakerData];
        handleFieldChange('speakers', updatedSpeakers);
        setSelectedEmployees([...selectedEmployees, employeeId]);
        setSelectEmployeeModal(false);
      }
    }
  };

  const removeSpeaker = (speakerId) => {
    const updatedSpeakers = speakers.filter(speaker => speaker.id !== speakerId);
    handleFieldChange('speakers', updatedSpeakers);
    setSelectedEmployees(selectedEmployees.filter(id => id !== speakerId));
  };

  const handleSpeakerAdded = (speakerData) => {
    // Determine speaker type
    const isFirst = speakers.length === 0;
    const type = isFirst ? 'main_speaker' : 'co_speaker';

    // Create speaker object with correct structure
    const newSpeaker = {
      id: speakerData.id || `temp-${Date.now()}`, // Temporary ID for new users
      // Properly handle userId and employeeId fields
      ...(speakerData.userId && { userId: speakerData.userId }),
      ...(speakerData.employeeId && { employeeId: speakerData.employeeId }),
      firstName: speakerData.firstName,
      lastName: speakerData.lastName,
      middleName: speakerData.middleName || '',
      title: speakerData.title || '',
      email: speakerData.email,
      department: '',
      image: speakerData.image,
      type: type,
      speakerType: speakerData.speakerType || 'user',
      isNewUser: speakerData.isNewUser || false
    };

    const updatedSpeakers = [...speakers, newSpeaker];
    handleFieldChange('speakers', updatedSpeakers);

    // Close both modals and reload if employee was added
    setAddSpeakerModal(false);
    setSelectEmployeeModal(false);
    if (speakerData.addedAsEmployee) {
      setTimeout(() => {
        reloadEmployees();
      }, 1500);
    }
  };

  const toggleSpeakerType = (speakerId, newType) => {
    const updatedSpeakers = speakers.map(speaker => {
      if (speaker.id === speakerId) {
        // Set the selected speaker to the new type
        return { ...speaker, type: newType };
      } else if (newType === 'main_speaker' && speaker.type === 'main_speaker') {
        // If we're setting someone as main_speaker, demote any existing main_speaker to co_speaker
        return { ...speaker, type: 'co_speaker' };
      } else {
        // Keep other speakers unchanged
        return speaker;
      }
    });
    handleFieldChange('speakers', updatedSpeakers);
  };

  const getSortedSpeakers = () => {
    return [...speakers].sort((a, b) => {
      const aIsMain = a.type === 'main_speaker';
      const bIsMain = b.type === 'main_speaker';
      
      if (aIsMain && !bIsMain) return -1;
      if (!aIsMain && bIsMain) return 1;
      return 0;
    });
  };

  // Get validation info from schema
  const stepValidation = getStepValidation('speakers');
  const requiredFields = getStepRequiredFields('speakers');
  const speakersFieldValidation = getFieldValidation('speakers');

  const handleContinue = () => {
    if (speakers.length > 0 && stepValidation.isValid) {
      onComplete?.();
      goToNextStep();
    }
  };

  const canContinue = speakers.length > 0 && stepValidation.isValid;

  return (
    <>
      <style>
        {`
          .dropdown-toggle-no-caret::after {
            display: none !important;
          }
        `}
      </style>
      <div className="d-flex flex-column h-100">
        <MDBContainer className="flex-grow-1 pb-5">
      <MDBTypography tag="h3" className="mb-4">
        {t('Choose Speakers')}
      </MDBTypography>
      <MDBTypography tag="p" className="mb-4 text-muted">
          {t('You can select multiple speakers. Speakers can be employees of your company or external speakers who are registered users.')}
        </MDBTypography>
      
      {/* Minimum speakers requirement notification */}
      {showMinSpeakersNotification && (
        <MDBCard className="mb-4 border-info">
          <MDBCardBody className="py-3">
            <div className="d-flex align-items-center">
              <MDBIcon fas icon="info-circle" className="me-3 text-info" size="lg" />
              <div>
                <MDBTypography tag="h6" className="mb-1 text-info">
                  {t('Speaker Requirement')}
                </MDBTypography>
                <MDBTypography tag="p" className="mb-0 text-muted">
                  {t('In the lecture category {{category}} at least {{count}} speakers are required', {
                    category: lectureTypeName,
                    count: minSpeakersRequired
                  })}
                </MDBTypography>
              </div>
            </div>
          </MDBCardBody>
        </MDBCard>
      )}
      
      {/* Wireframe Placeholder or Selected Speakers Cards */}
      <MDBRow className="mb-4">
        <MDBCol>
          {speakers.length > 0 ? (
            <>
              <MDBTypography tag="h5" className="mb-3">
                {t('Selected Speakers')}
              </MDBTypography>
              
              <MDBRow>
                {getSortedSpeakers().map((speaker) => {
                  const isMainSpeaker = speaker.type === 'main_speaker';
                  return (
                    <MDBCol key={speaker.id} size="12" md="6" lg="6" xl="6" xxl="4" className="mb-3">
                      <MDBCard className="h-100">
                        <MDBCardBody className="d-flex flex-column justify-content-center py-4">
                          <div className="d-flex justify-content-between align-items-start my-auto">
                            <div className="d-flex align-items-start flex-grow-1">
                              <div className="me-3 mb-3">
                                {speaker.image?.fileName ? (
                                  <img
                                    src={`https://iec-cp-public.s3.eu-central-1.amazonaws.com/userImages/${speaker.image.fileName}`}
                                    className="rounded-circle"
                                    style={{ height: "60px", width: "60px", objectFit: "cover" }}
                                    alt={speaker.image.alt || "Speaker"}
                                  />
                                ) : (
                                  <MDBIcon
                                    className="rounded-circle border d-flex align-items-center justify-content-center"
                                    style={{
                                      height: "60px",
                                      width: "60px",
                                      fontSize: "24px",
                                      backgroundColor: "#f8f9fa"
                                    }}
                                    fas
                                    icon="user-alt"
                                  />
                                )}
                              </div>
                              <div className="flex-grow-1">
                                <h6 className="mb-2 fw-bold">
                                  {speaker.title && `${speaker.title} `}
                                  {speaker.firstName} {speaker.middleName && `${speaker.middleName} `}{speaker.lastName}
                                </h6>
                                <div className="mb-2">
                                  {speaker.speakerType === 'user' ? (
                                    <MDBBadge color="info" light className="me-2">
                                      <MDBIcon fas icon="user" className="me-1" />
                                      {t('External Speaker')}
                                    </MDBBadge>
                                  ) : (
                                    <MDBBadge color="secondary" light className="me-2">
                                      <MDBIcon fas icon="building" className="me-1" />
                                      {t('Employee')}
                                    </MDBBadge>
                                  )}
                                </div>
                                {speaker.speakerType === 'user' && (() => {
                                  const options = affiliationOptions[speaker.userId || speaker.id];
                                  const affiliation = speaker.affiliation;
                                  const selectValue = affiliationSelectValue(affiliation);
                                  const showProfileOption = options
                                    ? (options.profileCompany || !options.profileReadable || selectValue === 'profile')
                                    : selectValue === 'profile';
                                  return (
                                    <div className="mb-3">
                                      <label className="form-label small text-muted mb-1">{t('Displayed company')}</label>
                                      <select
                                        className="form-select form-select-sm"
                                        value={selectValue}
                                        disabled={!options}
                                        onChange={(e) => handleAffiliationSelect(speaker, e.target.value)}
                                      >
                                        {!options && <option value="">{t('Loading...')}</option>}
                                        {(options?.companies || []).map(company => (
                                          <option key={company.id} value={`company:${company.id}`}>
                                            {company.name}{company.id === companyId ? ` (${t('lecture company')})` : ''}
                                          </option>
                                        ))}
                                        {showProfileOption && (
                                          <option value="profile">
                                            {t('From personal profile')}{options?.profileCompany ? `: ${options.profileCompany}` : ''}
                                          </option>
                                        )}
                                        <option value="manual">{t('Enter manually')}</option>
                                      </select>
                                      {affiliation?.source === 'manual' && (
                                        <MDBInput
                                          className="mt-2"
                                          size="sm"
                                          label={t('Company name')}
                                          value={affiliation.companyName || ''}
                                          onChange={(e) => setSpeakerAffiliation(speaker.id, { ...affiliation, companyName: e.target.value })}
                                        />
                                      )}
                                    </div>
                                  );
                                })()}
                                {speaker.department && (
                                  <small className="text-muted d-block mb-3">{speaker.department}</small>
                                )}
                                <MDBDropdown>
                                  <MDBDropdownToggle 
                                    tag='div'
                                    style={{ cursor: 'pointer' }}
                                    className="dropdown-toggle-no-caret"
                                  >
                                    <MDBBadge 
                                      color={isMainSpeaker ? "success" : "primary"} 
                                      light 
                                      className="px-3 py-2"
                                      style={{ fontSize: '0.875rem' }}
                                    >
                                      {isMainSpeaker ? t('Main Speaker') : t('Co-Speaker')}
                                      <MDBIcon icon="chevron-down" className="ms-1" size="sm" />
                                    </MDBBadge>
                                  </MDBDropdownToggle>
                                  <MDBDropdownMenu>
                                    <MDBDropdownItem 
                                      onClick={() => toggleSpeakerType(speaker.id, 'main_speaker')}
                                      className={`py-2 px-3 ${isMainSpeaker ? 'active' : ''}`}
                                      style={{ cursor: 'pointer' }}
                                    >
                                      <MDBIcon icon="star" className="me-2" />
                                      {t('Main Speaker')}
                                    </MDBDropdownItem>
                                    <MDBDropdownItem 
                                      onClick={() => toggleSpeakerType(speaker.id, 'co_speaker')}
                                      className={`py-2 px-3 ${!isMainSpeaker ? 'active' : ''}`}
                                      style={{ cursor: 'pointer' }}
                                    >
                                      <MDBIcon icon="user" className="me-2" />
                                      {t('Co-Speaker')}
                                    </MDBDropdownItem>
                                  </MDBDropdownMenu>
                                </MDBDropdown>
                              </div>
                            </div>
                            <MDBIcon
                              fas
                              icon="trash"
                              className="ms-2"
                              style={{
                                color: '#dc3545',
                                cursor: 'pointer',
                                fontSize: '16px',
                                padding: '4px',
                                transition: 'color 0.3s ease'
                              }}
                              onClick={() => removeSpeaker(speaker.id)}
                              onMouseEnter={(e) => e.target.style.color = '#b02a37'}
                              onMouseLeave={(e) => e.target.style.color = '#dc3545'}
                            />
                          </div>
                        </MDBCardBody>
                      </MDBCard>
                    </MDBCol>
                  );
                })}
                
                {/* Add Speaker Button - appears to the right of existing speakers */}
                <MDBCol size="12" md="6" lg="6" xl="6" xxl="4" className="mb-3">
                  <MDBCard 
                    className="h-100 border-2 border-dashed d-flex align-items-center justify-content-center"
                    style={{ 
                      borderColor: '#dee2e6',
                      backgroundColor: 'transparent',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease'
                    }}
                    onClick={toggleSelectEmployeeModal}
                  >
                    <MDBCardBody className="text-center">
                      <MDBBtn
                        color="primary"
                        floating
                        size="lg"
                        className="mb-3"
                        style={{ 
                          width: '60px', 
                          height: '60px',
                          borderRadius: '50%'
                        }}
                      >
                        <MDBIcon fas icon="plus" size="lg" />
                      </MDBBtn>
                      <MDBTypography 
                        tag="p" 
                        className="text-muted mb-0"
                        style={{ fontSize: '0.9rem' }}
                      >
                        {t('Add Speaker')}
                      </MDBTypography>
                    </MDBCardBody>
                  </MDBCard>
                </MDBCol>
              </MDBRow>
            </>
          ) : (
            <>
              <MDBTypography tag="h5" className="mb-3 text-muted">
                {t('Your Speakers')}
              </MDBTypography>
              <MDBRow>
                {/* Add First Speaker Card */}
                <MDBCol size="12" md="6" lg="6" xl="6" xxl="4" className="mb-3">
                  <MDBCard 
                    className="h-100 border-2 border-dashed d-flex align-items-center justify-content-center"
                    style={{ 
                      borderColor: '#dee2e6',
                      backgroundColor: 'transparent',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease'
                    }}
                    onClick={toggleSelectEmployeeModal}
                  >
                    <MDBCardBody className="text-center">
                      <MDBBtn
                        color="primary"
                        floating
                        size="lg"
                        className="mb-3"
                        style={{ 
                          width: '60px', 
                          height: '60px',
                          borderRadius: '50%'
                        }}
                      >
                        <MDBIcon fas icon="plus" size="lg" />
                      </MDBBtn>
                      <MDBTypography 
                        tag="p" 
                        className="text-muted mb-0"
                        style={{ fontSize: '0.9rem' }}
                      >
                        {t('Add your first speaker')}
                      </MDBTypography>
                    </MDBCardBody>
                  </MDBCard>
                </MDBCol>

                {/* Wireframe Cards */}
                {[2, 3].map((index) => (
                  <MDBCol key={index} size="12" md="6" lg="6" xl="6" xxl="4" className="mb-3">
                    <MDBCard 
                      className="h-100 border-0"
                      style={{
                        opacity: 1 - (index - 1) * 0.3,
                        transform: `translateX(${(index - 1) * 10}px)`,
                        backgroundColor: '#f8f9fa',
                        transition: 'all 0.3s ease'
                      }}
                    >
                      <MDBCardBody className="d-flex flex-column">
                        <div className="d-flex align-items-center mb-2">
                          <div 
                            className="rounded-circle me-3 bg-light border"
                            style={{
                              height: "50px",
                              width: "50px",
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}
                          >
                            <MDBIcon
                              fas
                              icon="user-alt"
                              style={{
                                fontSize: "20px",
                                color: '#dee2e6'
                              }}
                            />
                          </div>
                          <div className="flex-grow-1">
                            <div 
                              className="bg-light rounded mb-2"
                              style={{ height: '16px', width: '60%' }}
                            />
                            <div 
                              className="bg-light rounded mb-2"
                              style={{ height: '12px', width: '40%' }}
                            />
                            <div 
                              className="bg-light rounded"
                              style={{ 
                                height: '20px', 
                                width: index === 2 ? '80px' : '70px',
                                borderRadius: '12px'
                              }}
                            />
                          </div>
                        </div>
                      </MDBCardBody>
                    </MDBCard>
                  </MDBCol>
                ))}
              </MDBRow>
            </>
          )}
        </MDBCol>
      </MDBRow>


      {hasFieldError('speakers') && (
        <MDBRow className="mb-3">
          <MDBCol>
            <MDBAlert color="danger">
              {getFieldError('speakers')}
            </MDBAlert>
          </MDBCol>
        </MDBRow>
      )}

      {/* Validation Debug Info */}
      {isDevMode && (
        <MDBRow className="mb-4">
          <MDBCol>
            <MDBCard className="border-info">
              <MDBCardBody>
                <MDBTypography tag="h6" className="mb-3">
                  🔍 Validation Debug Info (Dev Mode) - Speakers Step
                </MDBTypography>
              
                <div className="mb-2">
                  <strong>Step Required Fields:</strong> {requiredFields.join(', ') || 'none'}
                </div>
                
                <div className="mb-2">
                  <strong>Current Speakers:</strong> {speakers.length} speaker(s) added
                  {speakers.length > 0 && (
                    <ul className="mb-0 mt-1">
                      {speakers.map((speaker, index) => (
                        <li key={index}>
                          {speaker.firstName} {speaker.lastName} 
                          {speaker.department && ` (${speaker.department})`}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
                
                <div className="mb-2">
                  <strong>Local Validation:</strong>{' '}
                  <MDBBadge color={speakers.length > 0 ? 'success' : 'warning'}>
                    {speakers.length > 0 ? `Valid (${speakers.length} speakers)` : 'Invalid (No speakers)'}
                  </MDBBadge>
                </div>
                
                <div className="mb-2">
                  <strong>Schema Validation:</strong>{' '}
                  <MDBBadge color={stepValidation.isValid ? 'success' : 'danger'}>
                    {stepValidation.isValid ? 'Valid' : 'Invalid'}
                  </MDBBadge>
                </div>
                
                {stepValidation.errors.length > 0 && (
                  <div className="mb-2">
                    <strong>Schema Errors:</strong>
                    <ul className="mb-0">
                      {stepValidation.errors.map((error, index) => (
                        <li key={index}>
                          <code>{error.field}</code>: {error.message}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                
                {speakersFieldValidation && (
                  <div className="mb-2">
                    <strong>Field-Specific Error:</strong>{' '}
                    <code>{speakersFieldValidation.field}</code>: {speakersFieldValidation.message}
                  </div>
                )}
                
                <div className="mb-2">
                  <strong>Continue Button:</strong>{' '}
                  <MDBBadge color={canContinue ? 'success' : 'warning'}>
                    {canContinue ? 'Enabled' : 'Disabled'}
                  </MDBBadge>
                </div>
                
                <div className="mb-2">
                  <strong>Company ID:</strong> {companyId || 'Not provided'}
                </div>
                
                <div className="mb-2">
                  <strong>Form Data Type:</strong> {formData.type || 'Not set'}
                </div>
                
                <div className="mb-2">
                  <strong>Min Speakers Required:</strong> {minSpeakersRequired}
                </div>
                
                <div className="mb-2">
                  <strong>Show Notification:</strong> {showMinSpeakersNotification ? 'Yes' : 'No'}
                </div>
                
                <div className="mb-2">
                  <strong>Lecture Type Name:</strong> {lectureTypeName || 'Not set'}
                </div>
              </MDBCardBody>
            </MDBCard>
          </MDBCol>
        </MDBRow>
      )}

        </MDBContainer>
        
        {/* Fixed button container at bottom */}
        <div className="bg-white" style={{ 
          position: 'sticky', 
          bottom: 0,
          padding: '1rem',
          marginTop: 'auto',
          zIndex: 10
        }}>
          <MDBContainer>
            <MDBRow>
              <MDBCol className="d-flex justify-content-center gap-5">
                <MDBBtn 
                  color="secondary" 
                  onClick={goToPreviousStep}
                  outline
                >
                  {t('Back')}
                </MDBBtn>
                <MDBBtn 
                  onClick={handleContinue}
                  disabled={!canContinue}
                >
                  {t('Continue')}
                </MDBBtn>
              </MDBCol>
            </MDBRow>
          </MDBContainer>
        </div>
      </div>

      {/* Employee Selection Modal */}
      <MDBModal open={selectEmployeeModal} onClose={() => setSelectEmployeeModal(false)}>
        <MDBModalDialog centered size="lg">
          <MDBModalContent>
            <MDBModalHeader>
              <MDBModalTitle>{t('Select Employee')}</MDBModalTitle>
              <MDBBtn className="btn-close" color="none" onClick={toggleSelectEmployeeModal}></MDBBtn>
            </MDBModalHeader>
            <MDBModalBody>
              <div className="mb-3">
                <MDBInputGroup>
                  <MDBIcon fas icon="search" className="input-group-text pt-2" />
                  <MDBInput
                    placeholder={t('Search employee')}
                    type="text"
                    value={employeeSearchInput}
                    onChange={handleChangeSearchEmployeeInput}
                  />
                </MDBInputGroup>
              </div>
              
              <div style={{ maxHeight: "500px", overflowY: "auto" }}>
                <MDBTable responsive className="mb-0">
                  <MDBTableBody>
                    {employees?.length > 0 && 
                      (filteredEmployees.length > 0 ? filteredEmployees : employees)
                        .filter(employee => !selectedEmployees.includes(employee.id))
                        .map((employee) => (
                          <tr key={employee.id} onClick={() => selectEmployee(employee.id)} style={{ cursor: "pointer" }}>
                            <td className="d-flex gap-3 align-items-center justify-content-between w-100">
                              <div className="d-flex gap-3 align-items-center flex-grow-1">
                                {(employee.image?.fileName || employee.user?.image?.fileName) ? (
                                  <img
                                    src={`https://iec-cp-public.s3.eu-central-1.amazonaws.com/userImages/${employee.image?.fileName || employee.user.image.fileName}`}
                                    className="rounded-circle border"
                                    style={{
                                      height: "50px",
                                      width: "50px",
                                      objectFit: "cover",
                                    }}
                                    alt={employee.image?.alt || employee.user.image.alt}
                                  />
                                ) : (
                                  <MDBIcon
                                    className="rounded-circle border"
                                    style={{
                                      height: "50px",
                                      width: "50px",
                                      textAlign: "center",
                                      paddingTop: "12px",
                                      fontSize: "20px",
                                      backgroundColor: "#f8f9fa"
                                    }}
                                    fas
                                    icon="user-alt"
                                  />
                                )}
                                <div>
                                  <h6 className="mb-1">
                                    {employee.user?.title && `${employee.user.title} `}
                                    {employee.user?.firstName} {employee.user?.middleName && `${employee.user.middleName} `}{employee.user?.lastName}
                                  </h6>
                                  <span className="text-muted">{employee.department?.items?.[0]?.department.name[0].text}</span>
                                </div>
                              </div>
                              <MDBBtn color="primary" size="sm">{t('Select')}</MDBBtn>
                            </td>
                          </tr>
                        ))
                    }
                  </MDBTableBody>
                </MDBTable>
                
                <div className="p-2">
                  <MDBBtn
                    onClick={toggleAddSpeakerModal}
                    color="primary"
                    outline
                    size="lg"
                    className="w-100 d-flex align-items-center justify-content-center py-3"
                    style={{
                      borderStyle: 'dashed',
                      borderWidth: '2px',
                      fontSize: '1rem',
                      minHeight: '70px'
                    }}
                  >
                    <MDBIcon fas icon="plus" className="me-2" size="lg" />
                    {t("Add speaker")}
                  </MDBBtn>
                </div>
              </div>
            </MDBModalBody>
            <MDBModalFooter>
              <MDBBtn color="secondary" onClick={toggleSelectEmployeeModal}>
                {t('Close')}
              </MDBBtn>
            </MDBModalFooter>
          </MDBModalContent>
        </MDBModalDialog>
      </MDBModal>
      
      {/* Add Speaker Modal */}
      <MDBModal open={addSpeakerModal} onClose={() => setAddSpeakerModal(false)} staticBackdrop>
        <MDBModalDialog centered size="lg" style={{zIndex: "9"}}>
          <MDBModalContent>
            <MDBModalHeader>
              <MDBModalTitle>{t("Add Speaker")}</MDBModalTitle>
              <MDBBtn className='btn-close' color='none' onClick={toggleAddSpeakerModal}></MDBBtn>
            </MDBModalHeader>
            <MDBModalBody>
              <AddSpeaker
                companyId={companyId}
                isActive={addSpeakerModal}
                onSpeakerAdded={handleSpeakerAdded}
              />
            </MDBModalBody>
          </MDBModalContent>
        </MDBModalDialog>
      </MDBModal>
    </>
  );
}